import { z } from 'zod';
import { CitySchema, SpecializationSchema } from './onboarding';

/**
 * Блок 11 «Личный кабинет», экран 2.11.2 — контракт редактирования профиля.
 * ADR-0091, PR `feature/block-11-profile-edit` (фаза 11.B, фронт-ферст на моках).
 *
 * Профиль = **ПДн** → региональный Postgres (P-007). Сервиса (Region API) ещё
 * нет → 11.B фронт на MSW-моке `PUT /api/profile`. Этот контракт задаёт форму
 * тела запроса/ответа. Любое изменение → новый ADR (P-011).
 *
 * ── Инвариант §1a (анти-спуфинг, P-009) ─────────────────────────────────────
 * `PUT /api/profile` — приватный (JWT обязателен). Пользователь берётся **из
 * JWT**, не из тела. Поэтому в `ProfileUpdateInputSchema` НЕТ:
 *   - `phone` — телефон = якорь входа/идентичности (P-010); меняется отдельным
 *     OTP-флоу (контракт с auth-бэком, задача 1.2), НЕ текстовым сохранением.
 *   - `region` — immutable после первой регистрации (P-008); не в теле.
 *   - серверных полей `userId`/`ts`/и т.п. — сервер берёт пользователя из JWT.
 * Лишние поля из тела клиента молча срезаются (default strip ZodObject —
 * паттерн `analytics.ts`/`cooperation.ts`): сервер игнорирует и перезаписывает
 * авторитетными значениями из JWT. Тип `ProfileUpdateInput` не может нести эти
 * поля — type-level защита (см. `@ts-expect-error` в profile.test.ts).
 *
 * ── Серверный инвариант город↔регион (в ADR; Zod один не выразит) ────────────
 * **`CITY_REGION_MAP[city] === user.region`**, иначе **отказ** (город нельзя
 * сменить так, чтобы сменился регион — P-008). `user.region` берётся из JWT,
 * поэтому проверка — на бэке (11.B: MSW-мок принимает только same-region).
 * Фронт дополнительно фильтрует дропдаун городов по своему региону (UI).
 *
 * `.trim()` на `name` — без него `"   "` проходит `.min(1)` и в Postgres попадает
 * whitespace-only ПДн (паттерн onboarding.ts, fixed inline там же).
 *
 * References:
 *   - ADR-0091 §1; PRD `15-block-11-cabinet.md` §2.11.2; `01-foundation.md` §1.7
 *   - `CitySchema`/`SpecializationSchema`: `contracts/onboarding.ts`
 *   - паттерн input-анти-спуфинг: `contracts/analytics.ts`, `contracts/cooperation.ts`
 *   - policies: P-007, P-008, P-009, P-010, P-011, P-012, P-013
 */

// ── Тело `PUT /api/profile` — БЕЗ phone/region/серверных полей (§1a) ──────────
export const ProfileUpdateInputSchema = z.object({
  name: z.string().trim().min(1).max(100),
  specialization: SpecializationSchema,
  city: CitySchema,
});
export type ProfileUpdateInput = z.infer<typeof ProfileUpdateInputSchema>;

// ── Ответ `PUT /api/profile` — эхо обновлённого профиля (для синка authStore) ──
// Телефон/регион в ответе не дублируем — они вне этого обновления (§1a).
export const ProfileUpdateResponseSchema = z.object({
  name: z.string(),
  specialization: SpecializationSchema,
  city: CitySchema,
});
export type ProfileUpdateResponse = z.infer<typeof ProfileUpdateResponseSchema>;
